import type { CommonArea, CommonAreaBooking } from '../../services/commonAreaReservationService';
import { bookingCalendarDate, operatingHoursForDate, wallClock, wholeDayLabel } from '../../pages/bookingWhen';

/**
 * How a reservation's when, who and where are written — in one place, because the operational
 * list, the área detail and the reservation modal all have to say the same thing about the same
 * booking, and they each used to carry their own copy.
 */

/**
 * The hours as WRITTEN in the timestamp — never `new Date(iso)`.
 *
 * A booking's times are a wall clock, not an instant: the clients send the hours the resident
 * picked with a `Z` on the end (and Android with -03:00, meaning the same thing). Re-reading that
 * through a Date shifts it into the browser's zone, so a 09:00 slot became 06:00 — hours nobody
 * chose, for a reservation the portaria has to honour.
 */
const fmtTime = (iso: string) => wallClock(iso) ?? '--:--';

/**
 * The DAY a booking is on, dd/mm/aa, read out of the ISO string's own calendar fields.
 *
 * Never `new Date(iso)`: a whole-day booking is stored as the wall clock 00:00:00–23:59:59 and is
 * not an instant, so re-reading it in the browser's zone moved a reservation for the 20th onto
 * the 19th. Same reasoning as pages/bookingWhen.ts, which this uses.
 */
export function bookingDayLabel(b: Pick<CommonAreaBooking, 'start_time'>): string {
  const date = bookingCalendarDate(b.start_time);
  if (!date) return '--';
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(2, 4)}`;
}

/** The same day written out — "20/10/2026" — for places with room for the century. */
export function bookingFullDayLabel(b: Pick<CommonAreaBooking, 'start_time'>): string {
  const date = bookingCalendarDate(b.start_time);
  if (!date) return '--';
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
}

/**
 * The hours to print. A whole-day booking shows the SPACE'S operating window — its stored
 * 00:00–23:59 is an artifact of "the whole day" that rendered as the nonsensical "21:00 – 20:59"
 * in UTC-3, and the window is what the resident actually gets. Time-slot bookings keep the
 * existing rendering: those are real hours somebody chose.
 */
export function bookingHoursLabel(
  b: Pick<CommonAreaBooking, 'start_time' | 'end_time'>,
  area?: CommonArea | null,
): string {
  const date = bookingCalendarDate(b.start_time);
  const hours = operatingHoursForDate(area, date);
  if (wholeDayLabel(b.start_time, b.end_time, area?.booking_mode, hours)) {
    return hours ? `${hours.open} às ${hours.close}` : 'Dia reservado';
  }
  return `${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}`;
}

/** yyyy-mm-dd of the booking, for grouping a month's reservations by day. */
export function bookingDateISO(b: Pick<CommonAreaBooking, 'start_time'>): string {
  return bookingCalendarDate(b.start_time) ?? '';
}

/**
 * The person this reservation can open, or null when there is nobody to open.
 *
 * The morador and the unidade on a row are the two things a síndico asks about next — "who is
 * this?" and "which apartment?" — and answering them meant leaving the list to go searching. They
 * are links now, but only where the row carries the id: a booking filed by a staff account with no
 * people row has a NAME and no `person_id`, and a dead link is worse than plain text.
 *
 * The embedded `person` wins over the flat `person_id`: it is the row the server actually resolved.
 */
export function bookingPersonLink(
  b: Pick<CommonAreaBooking, 'person_id' | 'person'>,
): string | null {
  const id = (b.person?.id || b.person_id || '').trim();
  return id || null;
}

/**
 * The unit this reservation can open, or null.
 *
 * `unit_label` alone is never enough: rows written before migration 217 froze the label without an
 * id, and that label is history — there is nothing to navigate to.
 */
export function bookingUnitLink(b: Pick<CommonAreaBooking, 'unit_id'>): string | null {
  const id = (b.unit_id || '').trim();
  return id || null;
}

/**
 * What a month-grid chip says about the time.
 *
 * A whole-day booking carries no hour anybody chose — it is the space for the day — so its stored
 * 00:00 is an artifact, and printing it beside the área's name says something false in the place a
 * síndico scans fastest ("00:00 Churrasqueira"). The chip has room for a word, not for a window,
 * so it names what the reservation IS; the chip's tooltip and the row below still carry the área's
 * operating hours, which is what somebody arriving actually needs.
 */
export function bookingChipTimeLabel(
  b: Pick<CommonAreaBooking, 'start_time' | 'end_time'>,
  area?: CommonArea | null,
): string {
  const date = bookingCalendarDate(b.start_time);
  const hours = operatingHoursForDate(area, date);
  // Falls back to the 00:00→23:59 span when the área is unknown, so a booking whose área is
  // missing from the list still reads correctly.
  if (wholeDayLabel(b.start_time, b.end_time, area?.booking_mode, hours)) return 'Dia inteiro';
  return fmtTime(b.start_time);
}
